import { Reducer } from 'redux';
import {
  DeviceActions,
  DeviceActionTypes,
  DevicesStore,
  DeviceStatus,
} from './deviceTypes';

const initialState: DevicesStore = {
  deviceList: null,
  syncDeviceError: null,
  error: null,
  isLoading: false,
  deviceSynced: false,
  shadowConfigs: null,
  shadowDeviceId: null,
  bbImages: null,
  selectedDevice: null,
  isNotesLoading: false,
  deviceNotes: null,
  noteActionStatus: {
    success: false,
    message: '',
    show: false,
  },
  actionStatus: {
    type: null,
    success: null,
  },
  deviceHistory: null,
  isHistoryLoading: false,
  deviceFaults: null,
  isFaultsLoading: false,
  isFaultsDeleting: false,
};

const devicesReducer: Reducer<DevicesStore, DeviceActions> = (
  state = initialState,
  action,
) => {
  switch (action.type) {
    case DeviceActionTypes.POPULATE_DEVICES: {
      const { deviceList } = action.payload;
      deviceList.forEach((device) => {
        device.owner_name =
          device.first_name && device.last_name
            ? `${device.first_name} ${device.last_name}`
            : '-';
        device.synced_by_customer_name = device.sync_code_done_by
          ? `${device.sync_code_done_by_first_name} ${device.sync_code_done_by_last_name}`
          : '-';
      });
      let newState = {
        ...state,
        isLoading: false,
        deviceList: deviceList,
      };
      return newState;
    }
    case DeviceActionTypes.CLEAR_DEVICE_LIST: {
      let newState = {
        ...state,
        deviceList: initialState.deviceList,
      };
      return newState;
    }
    case DeviceActionTypes.UPDATE_DEVICE_STATUS: {
      const { deviceSerialNumber, deviceStatus } = action.payload;
      let newState = {
        ...state,
        deviceList: (state.deviceList || []).map((device) => {
          if (device.serial_number === deviceSerialNumber) {
            return { ...device, status: deviceStatus as DeviceStatus };
          } else {
            return device;
          }
        }),
      };
      return newState;
    }
    case DeviceActionTypes.DEVICES_API_ERR: {
      const { error } = action.payload;
      console.log('error: ', error);
      let newState = {
        ...state,
        error: error,
      };
      return newState;
    }
    case DeviceActionTypes.DEVICES_SET_LOAD_STATE: {
      const { isLoading } = action.payload;
      let newState = {
        ...state,
        isLoading: isLoading,
      };
      return newState;
    }
    case DeviceActionTypes.FAILURE_SYNC_DEVICE: {
      const { syncDeviceError } = action.payload;
      let newState = {
        ...state,
        syncDeviceError: syncDeviceError,
      };
      return newState;
    }
    case DeviceActionTypes.CLEAR_SYNC_DEVICE_STATE: {
      let newState = {
        ...state,
        syncDeviceError: initialState.syncDeviceError,
        deviceSynced: initialState.deviceSynced,
      };
      return newState;
    }
    case DeviceActionTypes.DID_SYNC_DEVICE: {
      let newState = {
        ...state,
        deviceSynced: true,
      };
      return newState;
    }
    case DeviceActionTypes.STORE_DEVICE_SHADOW: {
      // Keep the owning device id so consumers can reject a slow response
      // landing after a hub switch (cross-hub shadow leak).
      const { deviceId, shadowConfigs } = action.payload;
      let newState = {
        ...state,
        shadowConfigs: shadowConfigs,
        shadowDeviceId: deviceId,
      };
      return newState;
    }
    case DeviceActionTypes.CLEAR_DEVICE_SHADOW: {
      let newState = {
        ...state,
        shadowConfigs: initialState.shadowConfigs,
        shadowDeviceId: initialState.shadowDeviceId,
      };
      return newState;
    }
    case DeviceActionTypes.STORE_BB_IMAGES: {
      const { bbImages } = action.payload;
      const newState = {
        ...state,
        bbImages,
      };
      return newState;
    }
    case DeviceActionTypes.FETCH_DEVICE_BY_ID: {
      const { device } = action.payload;
      return {
        ...state,
        selectedDevice: device,
      };
    }
    case DeviceActionTypes.UPDATE_SELECTED_DEVICE_STATUS: {
      const { status } = action.payload;
      if (state.selectedDevice) {
        return {
          ...state,
          selectedDevice: {
            ...state.selectedDevice,
            device_status: status,
          },
        };
      }
      return state;
    }

    case DeviceActionTypes.SET_NOTES_LOADING:
      return {
        ...state,
        isNotesLoading: action.payload.isLoading,
      };

    case DeviceActionTypes.FETCH_DEVICE_NOTES: {
      const { deviceNotes } = action.payload;
      return {
        ...state,
        deviceNotes: deviceNotes,
      };
    }
    case DeviceActionTypes.SET_NOTE_ACTION_STATUS:
      return {
        ...state,
        noteActionStatus: {
          ...action.payload,
          show: true,
        },
      };
    case DeviceActionTypes.CLEAR_NOTE_ACTION_STATUS:
      return {
        ...state,
        noteActionStatus: initialState.noteActionStatus,
      };
    case DeviceActionTypes.DEVICE_ACTION_SUCCESS:
      return {
        ...state,
        actionStatus: {
          type: action.payload.type,
          success: true,
        },
      };
    case DeviceActionTypes.DEVICE_ACTION_FAILED:
      return {
        ...state,
        actionStatus: {
          type: action.payload.type,
          success: false,
        },
      };
    case DeviceActionTypes.CLEAR_DEVICE_ACTION_STATUS:
      return {
        ...state,
        actionStatus: initialState.actionStatus,
      };
    case DeviceActionTypes.FETCH_DEVICE_HISTORY:
      return {
        ...state,
        deviceHistory: action.payload.history,
      };
    case DeviceActionTypes.SET_HISTORY_LOADING:
      return {
        ...state,
        isHistoryLoading: action.payload.isLoading,
      };

    case DeviceActionTypes.FETCH_DEVICE_FAULTS:
      return {
        ...state,
        deviceFaults: action.payload.deviceFaults,
      };
    case DeviceActionTypes.SET_FAULTS_LOADING:
      return {
        ...state,
        isFaultsLoading: action.payload.isLoading,
      };
    case DeviceActionTypes.SET_FAULTS_DELETING:
      return {
        ...state,
        isFaultsDeleting: action.payload.isDeleting,
      };

    default:
      return state;
  }
};

export default devicesReducer;
